import { toSuggestedMatchCandidates } from '@/composition/adapters/to-suggested-match-candidates';
import { useSessionListLinkPopoverHost } from '@/composition/hosts/patients/patient-find-or-link/useSessionListLinkPopoverHost';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { useFormatSessionTime } from '@/hooks/useFormatSessionTime';
import {
  ObjectWorkspaceShell,
  type ObjectWorkspaceObject,
  type ObjectWorkspaceShellProps,
  type ObjectWorkspaceWorkbenchContext,
} from '@heidi/agents';
import type { ObjectWorkspaceRoute } from '@heidi/agents/contracts';
import {
  artifactSheetOpenAtom,
  closeArtifactAtom,
  openArtifactAtom,
} from '@heidi/agents/data';
import { SessionListLinkPopover } from '@heidi/patients';
import { PatientSource } from '@heidi/schema/ml-scribe';
import { useQueryPagedLinkedSessionsByProfileId } from '@heidi/session/data';
import { useIsomorphicLayoutEffect } from '@heidi/utils';
import { useQueryUser } from '@heidi/workspace/data';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { navV3Messages } from '../nav-v3.messages';
import { useRecentVisits } from '../recent-visits/use-recent-visits';
import { resolvePatientDetailsState } from './object-workspace-patient-details';
import { resolvePatientSessionsState } from './object-workspace-patient-sessions';
import {
  toPatientObject,
  toVisitObject,
  uniqueObjects,
  uniqueSessions,
} from './object-workspace-route-objects';
import { ObjectWorkspaceRoutePending } from './object-workspace-route-pending';
import {
  useObjectWorkspaceSessionLookup,
  type WorkspaceSession,
} from './use-object-workspace-session-lookup';

interface ObjectWorkspaceRouteSurfaceProps {
  route: ObjectWorkspaceRoute;
  onClose: () => void;
  onObjectChange: (object: ObjectWorkspaceObject) => void;
  onUnavailable: () => void;
  renderWorkbench?: (
    context: ObjectWorkspaceWorkbenchContext & {
      isContextPanelOpen: boolean;
      closeContextPanel: () => void;
    }
  ) => ReturnType<NonNullable<ObjectWorkspaceShellProps['renderWorkbench']>>;
}

/** Resolves URL identity from the same session source used by Home and Recent Visits. */
export function ObjectWorkspaceRouteSurface({
  route,
  onClose,
  onObjectChange,
  onUnavailable,
  renderWorkbench,
}: ObjectWorkspaceRouteSurfaceProps): JSX.Element {
  const linkPopoverHost = useSessionListLinkPopoverHost();
  const intl = useIntl();
  const openArtifact = useAtomValue(openArtifactAtom);
  const isArtifactExplicitlyRevealed = useAtomValue(artifactSheetOpenAtom);
  const closeArtifact = useSetAtom(closeArtifactAtom);
  const updatedVisits = useRecentVisits('updatedAt', { pollEnabled: false });
  const createdVisits = useRecentVisits('createdAt', { pollEnabled: false });
  const visits = useMemo(
    () => uniqueSessions([...updatedVisits.visits, ...createdVisits.visits]),
    [createdVisits.visits, updatedVisits.visits]
  );
  const haveVisitFeedsSettled =
    (updatedVisits.hasResolvedData || updatedVisits.isError) &&
    (createdVisits.hasResolvedData || createdVisits.isError);
  const isFetching = updatedVisits.isFetching || createdVisits.isFetching;
  const isError = updatedVisits.isError || createdVisits.isError;
  const refetchUpdatedVisits = updatedVisits.refetch;
  const refetchCreatedVisits = createdVisits.refetch;
  const recentSession = visits.find((session) =>
    route.kind === 'visit'
      ? session.session_id === route.id
      : route.sourceSessionId
        ? session.session_id === route.sourceSessionId
        : session.patient_profile_id === route.id || session.session_id === route.id
  );
  const didMissRecentVisits = !recentSession && haveVisitFeedsSettled && !isFetching;
  // The URL omits sourceSessionId when it equals the patient id (no linked profile).
  const lookupSessionId =
    route.kind === 'visit' ? route.id : (route.sourceSessionId ?? route.id);
  const sessionLookup = useObjectWorkspaceSessionLookup(
    didMissRecentVisits ? lookupSessionId : undefined
  );
  const isLookupError = sessionLookup.isError;
  const isLookupSettledEmpty = sessionLookup.isMissing;
  const selectedSession: WorkspaceSession | undefined =
    recentSession ?? sessionLookup.session;
  const refetchSessionLookup = sessionLookup.refetch;
  const refetchVisits = useCallback(() => {
    void refetchUpdatedVisits();
    void refetchCreatedVisits();
    if (isLookupError) void refetchSessionLookup();
  }, [isLookupError, refetchCreatedVisits, refetchSessionLookup, refetchUpdatedVisits]);
  const {
    data: patientProfilesData,
    isLoading: arePatientProfilesLoading,
    isError: didPatientProfilesError,
  } = useQueryPatientProfiles({
    enabled: Boolean(selectedSession?.patient_profile_id),
  });
  const patientProfiles = useMemo(() => patientProfilesData ?? [], [patientProfilesData]);
  // A failed background refetch keeps cached profiles; only an empty cache is an error.
  const didPatientProfilesFail =
    didPatientProfilesError && patientProfilesData === undefined;
  const visitObjects = useMemo(
    () =>
      uniqueObjects(
        visits.map((session) => toVisitObject(session, intl, patientProfiles))
      ),
    [intl, patientProfiles, visits]
  );
  const patientObjects = useMemo(
    () => uniqueObjects(visits.map(toPatientObject)),
    [visits]
  );
  const resolvedObject = useMemo(
    () =>
      selectedSession
        ? route.kind === 'visit'
          ? toVisitObject(selectedSession, intl, patientProfiles)
          : toPatientObject(selectedSession)
        : null,
    [intl, patientProfiles, route.kind, selectedSession]
  );
  const routeIdentity = `${route.kind}:${route.id}:${route.sourceSessionId ?? ''}`;
  const [patientLinkAnchor, setPatientLinkAnchor] = useState<{
    element: HTMLElement;
    routeIdentity: string;
  } | null>(null);
  const [isPatientLinkFlowOpen, setIsPatientLinkFlowOpen] = useState(false);
  const enteredRouteIdentityRef = useRef<string | null>(null);
  const retainedObjectRef = useRef<{
    routeIdentity: string;
    object: ObjectWorkspaceObject;
  } | null>(null);

  if (resolvedObject) {
    retainedObjectRef.current = { routeIdentity, object: resolvedObject };
  }

  // A first turn can refresh the finite recent-session feeds and evict the current
  // session. Keep only an object already resolved for this exact URL identity.
  const object =
    resolvedObject ??
    (retainedObjectRef.current?.routeIdentity === routeIdentity
      ? retainedObjectRef.current.object
      : null);
  const patientSessionsProfileId =
    object?.kind === 'patient' ? (object.patientProfileId ?? '') : '';
  const patientSessions = useQueryPagedLinkedSessionsByProfileId(
    patientSessionsProfileId
  );
  const fetchNextPatientSessions = patientSessions.fetchNextPage;
  const showMorePatientSessions = useCallback(() => {
    void fetchNextPatientSessions();
  }, [fetchNextPatientSessions]);
  const { data: sessionDateFormat } = useQueryUser(
    (data) => data.session_settings?.date_format || undefined
  );
  const patientSource = patientSessionsProfileId
    ? patientProfiles.find((profile) => profile.id === patientSessionsProfileId)?.source
    : undefined;
  const formatSessionTime = useFormatSessionTime(patientSource !== PatientSource.EHR);

  // Object workspaces always start closed, even if Work Home left global
  // canvas state behind. The identity ref keeps the first render closed so a
  // stale panel cannot paint before the layout effect clears it.
  useIsomorphicLayoutEffect(() => {
    closeArtifact();
    enteredRouteIdentityRef.current = routeIdentity;
    return () => {
      enteredRouteIdentityRef.current = null;
      closeArtifact();
    };
  }, [closeArtifact, routeIdentity]);

  const isContextPanelOpen =
    enteredRouteIdentityRef.current === routeIdentity &&
    openArtifact !== null &&
    isArtifactExplicitlyRevealed;

  const handleAddPatient = useCallback(
    (anchorElement: HTMLElement) => {
      setPatientLinkAnchor({ element: anchorElement, routeIdentity });
      setIsPatientLinkFlowOpen(true);
    },
    [routeIdentity]
  );

  const handlePatientLinkFlowOpenChange = useCallback(
    (open: boolean) => {
      setIsPatientLinkFlowOpen(open);
      if (!open) patientLinkAnchor?.element.focus();
    },
    [patientLinkAnchor]
  );

  useEffect(() => {
    // Keep the object URL until the direct read confirms the session is gone; a
    // disabled feed (desktop not online-ready) or a failed fetch must not drop
    // the deep link.
    if (!object && didMissRecentVisits && isLookupSettledEmpty) onUnavailable();
  }, [didMissRecentVisits, isLookupSettledEmpty, object, onUnavailable]);

  if (!object) {
    return (
      <ObjectWorkspaceRoutePending
        isError={isError || isLookupError}
        onRetry={refetchVisits}
      />
    );
  }

  const activePatientLinkAnchor =
    patientLinkAnchor?.routeIdentity === routeIdentity ? patientLinkAnchor.element : null;
  const patientDetailsState = resolvePatientDetailsState({
    object,
    patientProfiles,
    isLoading: arePatientProfilesLoading,
    isError: didPatientProfilesFail,
  });
  const patientSessionsState = resolvePatientSessionsState({
    object,
    sessions: patientSessions.sessions,
    // Start times need the profile's source to pick wall-clock (EHR) or UTC.
    isLoading: patientSessions.isLoading || arePatientProfilesLoading,
    isError: patientSessions.isError || didPatientProfilesFail,
    hasMore: patientSessions.hasNextPage,
    isLoadingMore: patientSessions.isFetchingNextPage,
    onShowMore: showMorePatientSessions,
    untitledTitle: intl.formatMessage(navV3Messages.untitledSession),
    formatStartTime: (time) => formatSessionTime(time, sessionDateFormat),
  });

  return (
    <>
      <ObjectWorkspaceShell
        key={`${route.kind}:${route.id}`}
        object={object}
        patientDetailsState={patientDetailsState}
        patientSessionsState={patientSessionsState}
        isObjectControlled
        switcherOptions={route.kind === 'visit' ? visitObjects : patientObjects}
        onClose={onClose}
        onObjectChange={onObjectChange}
        onAddPatient={handleAddPatient}
        isContextPanelOpen={isContextPanelOpen}
        renderWorkbench={
          renderWorkbench
            ? (context) =>
                renderWorkbench({
                  ...context,
                  isContextPanelOpen,
                  closeContextPanel: closeArtifact,
                })
            : undefined
        }
      />
      {route.kind === 'visit' && !object.patient && activePatientLinkAnchor ? (
        <SessionListLinkPopover
          host={linkPopoverHost}
          anchorElement={activePatientLinkAnchor}
          open={isPatientLinkFlowOpen}
          onOpenChange={handlePatientLinkFlowOpenChange}
          selectedSessionIds={[object.id]}
          patientProfileAppointmentCandidates={
            selectedSession?.patient_profile_appointment_candidates
              ? toSuggestedMatchCandidates(
                  selectedSession.patient_profile_appointment_candidates
                )
              : undefined
          }
          popoverAlign="start"
          popoverSide="bottom"
          commandInputPlaceholder={intl.formatMessage({
            defaultMessage: 'Search for a patient',
            id: 'YHu74FB3mX',
            description: 'Placeholder in the encounter workspace patient-link search',
          })}
        />
      ) : null}
    </>
  );
}
